import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, ClipboardCheck, ClipboardList, Dumbbell, Gauge, Settings, Users } from "lucide-react";
import { api } from "../../../services/apiService";
import { useAuth } from "../../../context/AuthContext";
import { Badge } from "../../../components/ops/Badge";
import { EmptyState } from "../../../components/ops/EmptyState";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Sparkbars } from "../../../components/ops/Sparkbars";
import { StatCard } from "../../../components/ops/StatCard";
import { todayLabel } from "../../../utils/greeting";
import { countSince, dailyCounts } from "../../../utils/trends";

const quickActions = [
  { to: "/admin/programs", label: "Create a program", description: "Publish a new age-based training program", icon: Dumbbell },
  { to: "/admin/assessments", label: "Record an assessment", description: "Score a player's latest evaluation", icon: ClipboardList },
  { to: "/admin/schedule", label: "Schedule a session", description: "Open a new training slot for booking", icon: CalendarClock },
  { to: "/admin/players", label: "Review players", description: "Browse the full player roster", icon: Users },
  { to: "/admin/settings", label: "Account settings", description: "Update your admin profile", icon: Settings },
];

export function AdminDashboardPage() {
  const { accessToken } = useAuth();
  const [players, setPlayers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([api.get("/dashboard/summary"), api.get("/programs/manage/all")])
      .then(([summaryResponse, programsResponse]) => {
        if (!active) return;
        setPlayers(summaryResponse.data.players || []);
        setPrograms(programsResponse.data.programs || []);
      })
      .catch(() => { if (active) setError("Unable to load dashboard data"); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [accessToken]);

  const stats = useMemo(() => {
    const assessed = players.filter((entry) => entry.latestAssessment);
    const avgIndex = assessed.length
      ? Math.round(assessed.reduce((sum, entry) => sum + (entry.formulaPlayerIndex || 0), 0) / assessed.length)
      : null;
    const unassessed = players.length - assessed.length;
    const draftPrograms = programs.filter((program) => !program.published).length;
    const playerDocs = players.map((entry) => entry.player);
    const newPlayersThisWeek = countSince(playerDocs);

    return {
      totalPlayers: players.length,
      activePrograms: programs.filter((program) => program.published).length,
      totalPrograms: programs.length,
      assessedCount: assessed.length,
      unassessed,
      draftPrograms,
      newPlayersThisWeek,
      avgIndex,
    };
  }, [players, programs]);

  const signupsByDay = useMemo(
    () => dailyCounts(players.map((entry) => entry.player)),
    [players],
  );

  const recentPlayers = useMemo(
    () => [...players]
      .sort((a, b) => new Date(b.player?.createdAt || 0) - new Date(a.player?.createdAt || 0))
      .slice(0, 5),
    [players],
  );

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={`${todayLabel()} · Overview of Formula Soccer operations`} />

      <div className="ops-stat-grid">
        <StatCard
          icon={Users}
          label="Total players"
          value={loading ? "—" : stats.totalPlayers}
          hint="Across all programs"
          trend={!loading && stats.newPlayersThisWeek > 0 ? { label: `+${stats.newPlayersThisWeek} this week` } : null}
        />
        <StatCard icon={Dumbbell} label="Active programs" value={loading ? "—" : stats.activePrograms} hint={`${stats.totalPrograms} total`} />
        <StatCard icon={ClipboardCheck} label="Players assessed" value={loading ? "—" : stats.assessedCount} hint={`of ${stats.totalPlayers} players`} />
        <StatCard icon={Gauge} label="Avg. Formula Player Index" value={loading ? "—" : stats.avgIndex ?? "—"} hint="Latest assessments" />
      </div>

      {error && <div className="ops-alert ops-alert--error">{error}</div>}

      <div className="ops-columns">
        <div className="ops-card">
          <h2 className="ops-card-title">Recently added players</h2>
          {loading ? (
            <p className="ops-loading">Loading players…</p>
          ) : recentPlayers.length === 0 ? (
            <EmptyState icon={Users} title="No players yet" description="Players will show up here once they're registered." />
          ) : (
            <ul className="ops-list">
              {recentPlayers.map(({ player, formulaPlayerIndex, attendanceRate }) => (
                <li key={player._id} className="ops-list-item">
                  <div className="ops-list-item-avatar">
                    {player.firstName?.charAt(0)}{player.lastName?.charAt(0)}
                  </div>
                  <div className="ops-list-item-body">
                    <p className="ops-list-item-title">{player.firstName} {player.lastName}</p>
                    <p className="ops-list-item-meta">
                      Ages {player.ageGroup} · {attendanceRate != null ? `${attendanceRate}% attendance` : "No attendance yet"}
                    </p>
                  </div>
                  <Badge tone={formulaPlayerIndex != null ? "success" : "neutral"}>
                    {formulaPlayerIndex != null ? `FPI ${formulaPlayerIndex}` : "Not assessed"}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          <Link to="/admin/players" className="ops-card-link">
            View all players <ArrowRight size={14} />
          </Link>
        </div>

        <div className="ops-stack">
          <div className="ops-card">
            <div className="ops-activity-header">
              <h2 className="ops-card-title" style={{ margin: 0 }}>New signups</h2>
              {!loading && <Sparkbars data={signupsByDay} />}
            </div>
            <p className="ops-activity-total">
              <strong>{loading ? "—" : stats.newPlayersThisWeek}</strong> {stats.newPlayersThisWeek === 1 ? "player" : "players"} registered in the last 7 days
            </p>

            <ul className="ops-attention-list">
              {!loading && stats.unassessed > 0 && (
                <li>
                  <Link to="/admin/assessments" className="ops-attention-item">
                    <span className="ops-attention-item-label">
                      <span className="ops-attention-item-dot" />
                      {stats.unassessed} {stats.unassessed === 1 ? "player" : "players"} awaiting assessment
                    </span>
                    <ArrowRight size={14} />
                  </Link>
                </li>
              )}
              {!loading && stats.draftPrograms > 0 && (
                <li>
                  <Link to="/admin/programs" className="ops-attention-item">
                    <span className="ops-attention-item-label">
                      <span className="ops-attention-item-dot" />
                      {stats.draftPrograms} draft {stats.draftPrograms === 1 ? "program" : "programs"} unpublished
                    </span>
                    <ArrowRight size={14} />
                  </Link>
                </li>
              )}
              {!loading && stats.unassessed === 0 && stats.draftPrograms === 0 && (
                <li className="ops-attention-empty">You're all caught up — nothing needs attention.</li>
              )}
            </ul>
          </div>

          <div className="ops-card">
            <h2 className="ops-card-title">Quick actions</h2>
            <div className="ops-action-grid">
              {quickActions.map(({ to, label, description, icon: Icon }) => (
                <Link key={to} to={to} className="ops-action-card">
                  <div className="ops-action-card-icon">
                    <Icon size={20} strokeWidth={1.8} />
                  </div>
                  <div className="ops-action-card-content">
                    <h3>{label}</h3>
                    <p>{description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
